
// NormalDatePickerStep.jsx
import { useMemo, useState, useEffect, useRef } from "react";
import moment from "moment-jalaali";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Lock,
  CheckCircle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  AlertCircle,
  User,
} from "lucide-react";
import { toast } from "react-toastify";

const persianMonths = [
  { id: 1, name: "فروردین", days: 31 },
  { id: 2, name: "اردیبهشت", days: 31 },
  { id: 3, name: "خرداد", days: 31 },
  { id: 4, name: "تیر", days: 31 },
  { id: 5, name: "مرداد", days: 31 },
  { id: 6, name: "شهریور", days: 31 },
  { id: 7, name: "مهر", days: 30 },
  { id: 8, name: "آبان", days: 30 },
  { id: 9, name: "آذر", days: 30 },
  { id: 10, name: "دی", days: 30 },
  { id: 11, name: "بهمن", days: 30 },
  { id: 12, name: "اسفند", days: 29 },
];

const weekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

const reasonLabelMap = {
  SALON_HOLIDAY: "تعطیلی سالن",
  FULLY_BOOKED: "ظرفیت تکمیل",
  STAFF_NOT_WORKING: "هیچ نیرویی کار نمی‌کند",
};

const isLeapYear = (year) => {
  const breaks = [1, 5, 9, 13, 17, 22, 26, 30];
  return breaks.includes(year % 33);
};

const getDaysInMonth = (month, year) => {
  if (!month || month < 1 || month > 12) return 30;
  if (month === 12 && isLeapYear(year)) return 30;
  return persianMonths[month - 1].days;
};

const NormalDatePickerStep = ({
  availabilityData,
  loading,
  error,
  selectedDate,
  onSelectDate,
  onBack,
  onContinue,
  axiosAdmin,
  serviceId,
  type,
}) => {
  const containerRef = useRef(null);
  const timeSectionRef = useRef(null);

  const [internalSelectedDate, setInternalSelectedDate] = useState(selectedDate || null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null); // کارمند منتخب

  const [dayDetail, setDayDetail] = useState(null);
  const [fetchingDay, setFetchingDay] = useState(false);

  const serverToday = availabilityData?.dateNowServer?.today || moment().format("YYYY-MM-DD");
  const initialMoment = moment(serverToday, "YYYY-MM-DD");
  const [selectedYear, setSelectedYear] = useState(initialMoment.jYear());
  const [selectedMonth, setSelectedMonth] = useState(initialMoment.jMonth() + 1);

  // scroll to top on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // build maps from availabilityData
  const availableMap = useMemo(() => {
    const map = {};
    (availabilityData?.availability?.availableDates || []).forEach((item) => {
      map[item.date] = item;
    });
    return map;
  }, [availabilityData]);

  const unavailableMap = useMemo(() => {
    const map = {};
    (availabilityData?.availability?.unavailableDates || []).forEach((item) => {
      map[item.date] = item;
    });
    return map;
  }, [availabilityData]);

  const currentMonthName =
    selectedMonth >= 1 && selectedMonth <= 12
      ? persianMonths[selectedMonth - 1].name
      : "نامشخص";

  const changeMonth = (direction) => {
    if (direction === "next") {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear((prev) => prev + 1);
      } else {
        setSelectedMonth((prev) => prev + 1);
      }
    } else {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear((prev) => prev - 1);
      } else {
        setSelectedMonth((prev) => prev - 1);
      }
    }
  };

  const generateCalendarDays = () => {
    if (!selectedMonth || !selectedYear) return [];
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDayOfMonth = moment(`${selectedYear}/${selectedMonth}/1`, "jYYYY/jM/jD");
    const startDayOfWeek = firstDayOfMonth.day() + 1;
    const days = [];

    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const jalaaliDate = moment(`${selectedYear}/${selectedMonth}/${day}`, "jYYYY/jM/jD");
      const gregorianDate = jalaaliDate.format("YYYY-MM-DD");
      const availableItem = availableMap[gregorianDate];
      const unavailableItem = unavailableMap[gregorianDate];

      days.push({
        day,
        jalaaliDate: jalaaliDate.format("jYYYY/jMM/jDD"),
        gregorianDate,
        isToday: gregorianDate === serverToday,
        isAvailable: Boolean(availableItem),
        isUnavailable: Boolean(unavailableItem),
        isSelected: internalSelectedDate?.date === gregorianDate,
        availableItem,
        unavailableItem,
      });
    }
    return days;
  };

  const calendarDays = generateCalendarDays();

  // انتخاب روز
  const handleSelectDay = (dateObj) => {
    if (!dateObj || !dateObj.isAvailable) return;

    const datePayload = {
      date: dateObj.gregorianDate,
      jalaaliDate: dateObj.jalaaliDate,
    };
    setInternalSelectedDate(datePayload);
    setSelectedTime(null);
    setSelectedStaff(null); // پاک کردن کارمند قبلی
    if (onSelectDate) onSelectDate(datePayload);

    setTimeout(() => {
      if (timeSectionRef.current) {
        timeSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  // دریافت جزئیات روز
  useEffect(() => {
    if (!internalSelectedDate || !axiosAdmin || !serviceId || !type) return;

    const fetchDay = async () => {
      setFetchingDay(true);
      setDayDetail(null);
      try {
        const res = await axiosAdmin.get("/api/admin/appointment/day-availability-normal", {
          params: {
            serviceId,
            type,
            date: internalSelectedDate.date,
          },
        });
        setDayDetail(res.data);
      } catch (err) {
        console.error("Error fetching normal day availability:", err);
        toast.error("خطا در دریافت جزئیات روز");
      } finally {
        setFetchingDay(false);
      }
    };
    fetchDay();
  }, [internalSelectedDate, axiosAdmin, serviceId, type]);

  // الگوریتم انتخاب کارمند بر اساس کمترین قرارهای فعال
  const pickStaffForTime = (staffs) => {
    if (!staffs || staffs.length === 0) return null;

    const minCount = Math.min(...staffs.map((s) => s.appointmentCount));
    const candidates = staffs.filter((s) => s.appointmentCount === minCount);

    // اگر چند کاندیدا بودند، یکی را رندوم انتخاب کن
    const chosen = candidates[Math.floor(Math.random() * candidates.length)];

    console.log(`👤 کارمند منتخب برای تایم: ${chosen.displayName} (قرارهای فعال: ${chosen.appointmentCount})`);
    return chosen;
  };

  // انتخاب تایم
  const handleSelectTime = (time) => {
    // پیدا کردن timeSlot متناظر
    const slot = dayDetail?.timeSlots?.find((s) => s.time === time);
    if (!slot || !slot.available) return;

    const staffs = slot.staffs;
    const staff = pickStaffForTime(staffs);

    setSelectedTime(time);
    setSelectedStaff(staff || null);
  };

  const getUnavailableReasonText = (dateObj) => {
    const reasons = dateObj?.unavailableItem?.reasons || [];
    if (!reasons.length) return "غیرقابل رزرو";
    return reasons.map((r) => reasonLabelMap[r.code] || r.message).join("، ");
  };

  const handleContinue = () => {
    if (!internalSelectedDate || !selectedTime || !selectedStaff) return;
    onContinue({
      date: internalSelectedDate.date,
      jalaaliDate: internalSelectedDate.jalaaliDate,
      time: selectedTime,
      staff: {
        _id: selectedStaff._id,
        displayName: selectedStaff.displayName,
        phone: selectedStaff.phone,
        appointmentCount: selectedStaff.appointmentCount,
      },
    });
  };

  if (loading) {
    return (
      <div className="min-h-[500px] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-9 h-9 text-purple-600 animate-spin" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-2">در حال بررسی ظرفیت رزرو</h3>
          <p className="text-sm text-slate-500">لطفاً چند لحظه صبر کنید...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[500px] flex items-center justify-center">
        <div className="max-w-md w-full bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-red-700 mb-2">خطا در دریافت ظرفیت</h3>
          <p className="text-sm text-red-600 leading-7">{error}</p>
          <button
            onClick={onBack}
            className="mt-5 px-5 py-2.5 rounded-xl bg-white border border-red-200 text-red-600 hover:bg-red-100 transition-colors"
          >
            بازگشت
          </button>
        </div>
      </div>
    );
  }

  if (!availabilityData) {
    return (
      <div className="min-h-[500px] flex items-center justify-center">
        <div className="text-center text-slate-500">اطلاعاتی برای نمایش وجود ندارد.</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 via-purple-600 to-fuchsia-600 rounded-2xl p-5 shadow-lg shadow-purple-500/25 overflow-hidden relative">
        <div className="absolute -top-10 -left-10 w-32 h-32 rounded-full bg-white/10" />
        <div className="absolute -bottom-16 -right-16 w-44 h-44 rounded-full bg-white/10" />
        <div className="relative z-10 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-6 h-6 text-white" />
            <h3 className="text-xl font-bold text-white">انتخاب تاریخ رزرو عادی</h3>
          </div>
          <p className="text-purple-100 text-sm leading-7">
            روزی که قصد رزرو دارید را انتخاب کنید. تایم‌های خالی همه‌ی نیروها نمایش داده می‌شود.
          </p>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {/* Calendar Header */}
        <div className="bg-slate-900 px-4 md:px-5 py-4 flex items-center justify-between">
          <button
            onClick={() => changeMonth("prev")}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
          <div className="text-center">
            <h3 className="text-lg md:text-xl font-black text-white">
              {currentMonthName} {selectedYear}
            </h3>
            <p className="text-[10px] md:text-xs text-slate-300 mt-1">
              {isLeapYear(selectedYear) ? "سال کبیسه" : "سال عادی"}
            </p>
          </div>
          <button
            onClick={() => changeMonth("next")}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Week Days */}
        <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
          {weekDays.map((day) => (
            <div key={day} className="py-2 md:py-3 text-center text-xs md:text-sm font-bold text-slate-600">
              {day}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 gap-1 md:gap-2 p-2 md:p-4 bg-white">
          {calendarDays.map((dateObj, index) => {
            if (!dateObj) {
              return <div key={`empty-${index}`} className="aspect-square" />;
            }
            const disabled = !dateObj.isAvailable;

            return (
              <button
                key={dateObj.gregorianDate}
                type="button"
                disabled={disabled}
                onClick={() => handleSelectDay(dateObj)}
                title={disabled ? getUnavailableReasonText(dateObj) : "قابل رزرو"}
                className={`
                  relative aspect-square rounded-xl md:rounded-2xl border transition-all duration-300 flex flex-col items-center justify-center gap-0.5
                  text-[12px] md:text-base font-black
                  ${
                    dateObj.isSelected
                      ? "border-purple-600 bg-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105"
                      : dateObj.isAvailable
                      ? "border-green-200 bg-white hover:bg-green-50 hover:border-green-400 hover:shadow-md cursor-pointer"
                      : "border-slate-200 bg-slate-100 text-slate-400 opacity-60 cursor-not-allowed"
                  }
                `}
              >
                {dateObj.isToday && (
                  <span className={`absolute top-1 right-1 w-2 h-2 rounded-full ${dateObj.isSelected ? "bg-white" : "bg-blue-500"}`} />
                )}
                {disabled && (
                  <span className="absolute top-1 left-1">
                    <Lock className="w-3 h-3 md:w-3.5 md:h-3.5 text-slate-400" />
                  </span>
                )}
                {!disabled && !dateObj.isSelected && (
                  <span className="absolute top-1 left-1 hidden md:block">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  </span>
                )}
                <span className="text-base md:text-lg">{dateObj.day}</span>
                {!dateObj.isAvailable && (
                  <span className="hidden md:inline text-[9px] md:text-[10px] px-0.5 text-center leading-4 line-clamp-2">
                    {getUnavailableReasonText(dateObj)}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Bottom Navigation Buttons */}
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={() => changeMonth("prev")}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium shadow-sm"
          >
            <ArrowRight className="w-4 h-4" />
            <span>ماه قبل</span>
          </button>
          <button
            onClick={() => changeMonth("next")}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium shadow-sm"
          >
            <span>ماه بعد</span>
            <ArrowLeft className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Time Selection Section */}
      {internalSelectedDate && (
        <div ref={timeSectionRef} className="space-y-4">
          <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-2xl p-4">
            <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-purple-800">
                تاریخ انتخاب‌شده: {internalSelectedDate.jalaaliDate}
              </p>
            </div>
          </div>

          {fetchingDay ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
              <span className="mr-2 text-slate-600">دریافت ساعات...</span>
            </div>
          ) : dayDetail ? (
            dayDetail.isAvailable ? (
              <div>
                <h4 className="text-sm font-bold text-slate-700 mb-3">یک ساعت را انتخاب کنید</h4>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {dayDetail.availableTimes.map((time) => {
                    const isSelected = selectedTime === time;
                    return (
                      <button
                        key={time}
                        type="button"
                        onClick={() => handleSelectTime(time)}
                        className={`
                          relative rounded-xl border-2 p-2 text-center transition-all duration-200
                          ${isSelected
                            ? "border-purple-600 bg-purple-50 shadow-md shadow-purple-200"
                            : "border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-sm"
                          }
                        `}
                      >
                        <span className={`text-sm font-bold ${isSelected ? "text-purple-700" : "text-slate-800"}`}>
                          {time}
                        </span>
                        {isSelected && (
                          <span className="absolute -top-1.5 -right-1.5 bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">
                            ✓
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* نمایش کارمند انتخاب‌شده */}
                {selectedStaff && (
                  <div className="mt-4 flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-xl p-3">
                    <User className="w-5 h-5 text-purple-600" />
                    <span className="text-sm font-medium text-purple-800">
                      نیروی انتخاب‌شده: {selectedStaff.displayName}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
                <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm text-amber-800 font-medium">
                  این روز تایم خالی ندارد. روز دیگری انتخاب کنید.
                </p>
              </div>
            )
          ) : (
            <div className="text-center text-slate-500 py-4">اطلاعات ساعتی برای این روز در دسترس نیست.</div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="xm:px-5 px-2 py-2.5 xm:text-base text-sm rounded-xl bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
        >
          <ArrowRight className="w-4 h-4" />
          بازگشت
        </button>

        <button
          type="button"
          disabled={!internalSelectedDate || !selectedTime || !selectedStaff}
          onClick={handleContinue}
          className="xm:px-6 px-3 py-2.5 xm:text-base text-sm rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 text-white font-medium shadow-md shadow-purple-500/25 hover:shadow-lg hover:shadow-purple-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <span className="45xm:block hidden">تأیید رزرو و پرداخت</span>
          <span className="45xm:hidden block"> تأیید و پرداخت</span>
          <span>←</span>
        </button>
      </div>

    </div>
  );
};

export default NormalDatePickerStep;